'use client';

import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Well from '@/components/ui/Well';
import Buttons from '@/components/ui/Buttons';
import Button from '@/components/ui/Button';
import { IMarketingListToolFilter } from '@/components/content/MarketingListTool/interfaces/marketingListToolFilter.interface';
import { companyRequestBody, personRequestBody } from '@/lib/helpers/marketingList';
import { downloadFile } from '@/lib/company/downloadFile';
import { AuthContext, User } from '@/components/auth/AuthProvider';
import { InfopankMarketingSearchLimits } from '@/apiTypes/types';
import { useOverlay } from '@/components/hooks/useOverlay';
import { OverlayKey } from '@/lib/enums/overlayKey.enum';
import LoadingContent from '@/components/ui/LoadingContent';
import { useTRPC } from '@/trpc/client';
import { downloadUrlQueryOptions, limitsQueryOptions, marketingKeys } from '@/lib/queries/marketingQueries';
import { useToaster } from '@/components/hooks/useToaster';

export type MarketingListExportType = 'Person' | 'Company';

interface IProps {
  total: number;
  user: User | null;
  filter: IMarketingListToolFilter;
  type: MarketingListExportType;
  isTotalLoaded: boolean;
}

export const initialData: InfopankMarketingSearchLimits = {
  maxRowsInExport: 0,
  exportLimitType: 'None',
  maxAllowedExports: 0,
  totalExportsLeft: 0,
  totalExportsUsed: 0,
  exportPrice: 0,
};

export const getPayload = (type: MarketingListExportType, filter: IMarketingListToolFilter) => {
  return type === 'Person' ? personRequestBody(filter) : companyRequestBody(filter);
};

export const DownloadResults = (props: IProps) => {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { orderCode, orderInitialized } = React.useContext(AuthContext);
  const { openOverlay: openExtraDownloadOverlay } = useOverlay(OverlayKey.marketing_list_extra_download);
  const { openOverlay: openLimitDownloadOverlay } = useOverlay(OverlayKey.limit_download_paywall);
  const { openToast } = useToaster();
  const [loading, setLoading] = React.useState(false);

  const downloadUrlQuery = useQuery(downloadUrlQueryOptions({ trpc, type: props.type }));
  const downloadUrl = downloadUrlQuery.data ?? '#';

  const limitsQuery = useQuery(limitsQueryOptions({ trpc, enabled: orderInitialized }));
  const limits = limitsQuery.data ?? initialData;
  const supportingError = downloadUrlQuery.error ?? limitsQuery.error;
  const downloadUnavailable = downloadUrlQuery.isPending || limitsQuery.isPending || supportingError !== null;

  React.useEffect(() => {
    if (supportingError) {
      console.error('failed to prepare marketing list download', supportingError);
    }
  }, [supportingError]);

  return (
    <div>
      <Well theme="medium">
        {props.isTotalLoaded ? (
          <p className="text-size-large bold">{t('company.download.result', { total: props.total })}</p>
        ) : (
          <LoadingContent size="small" />
        )}
        {limitsQuery.isSuccess && <p>{t('company.download.description', { maxRowsInExport: limits.maxRowsInExport })}</p>}
        <Buttons>
          <Button
            title={t('company.download.save')}
            iconLeft="document-download"
            theme="primary"
            size="large"
            loading={loading}
            disabled={loading || downloadUnavailable}
            onClick={() => {
              if (limits.exportLimitType === 'UpperLimit' || limits.totalExportsLeft === 0) {
                openLimitDownloadOverlay();
              } else if (limits.exportLimitType === 'ExtraDownload') {
                openExtraDownloadOverlay();
              } else if (limits.totalExportsLeft > 0) {
                setLoading(true);
                void downloadFile(
                  downloadUrl,
                  props.user,
                  orderCode,
                  'POST',
                  JSON.stringify(getPayload(props.type, props.filter)),
                  false,
                  true,
                )
                  .catch((error) => {
                    console.error('marketing list download failed', error);
                    openToast({
                      id: 'marketing-download-failed',
                      text: t('components.error.title'),
                      description: t('components.error.description'),
                      intent: 'danger',
                    });
                  })
                  .finally(() => {
                    setLoading(false);
                    void queryClient.invalidateQueries({ queryKey: marketingKeys.limits(trpc) });
                  });
              }
            }}
          />
        </Buttons>
      </Well>
      {limitsQuery.isSuccess && (
        <p>
          {t('company.download.limit', {
            limit: limits.maxAllowedExports,
            stock: limits.totalExportsLeft,
          })}
        </p>
      )}
    </div>
  );
};
